import { typedSupabase } from "../lib/typed-supabase";
import { NOTIFICATION_TYPES } from "../types/notification";
import { processMentions } from "../utils/mentionUtils";
import { notificationManager } from "./notifications/notification-manager.service";
import { notificationPreferencesService } from "./notifications/notification-preferences.service";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import { logger } from "../lib/logger";
export interface TaskComment {
    id: string;
    task_id: string;
    user_id: string;
    content: string;
    created_at: string;
    updated_at: string;
    principal_id?: string | null;
    user?: {
        username: string | undefined;
        full_name: string | undefined;
        avatar_url: string | undefined;
    };
    principal?: {
        display_name: string | null;
        kind: string | null;
    } | null;
}
export interface CreateTaskCommentRequest {
    task_id: string;
    content: string;
}
// ============================================================================
// TASK COMMENT SERVICE - Phase 4 Architecture
// ============================================================================
export interface ITaskCommentService extends IService {
    getTaskComments(taskId: string): Promise<ServiceResponse<TaskComment[]>>;
    createTaskComment(comment: CreateTaskCommentRequest): Promise<ServiceResponse<TaskComment>>;
    updateTaskComment(id: string, content: string): Promise<ServiceResponse<TaskComment>>;
    deleteTaskComment(id: string): Promise<ServiceResponse<void>>;
}
/**
 * Helper to create mention notifications with preference checks
 */
async function createMentionNotifications(mentionedUsers: Awaited<ReturnType<typeof processMentions>>["mentionedUsers"], userId: string, taskId: string, commentId: string, content: string, actorData: {
    username?: string | null;
    full_name?: string | null;
    avatar_url?: string | null;
} | null, taskTitle?: string | null): Promise<void> {
    if (mentionedUsers.length === 0)
        return;
    const commentPreview = content.length > 50 ? `${content.substring(0, 50)}...` : content;
    // Create notifications for each mentioned user, checking preferences
    const notificationPromises = mentionedUsers.map(async (mentionedUser) => {
        // Skip self-mentions (consistent with community comments behavior)
        if (mentionedUser.id === userId)
            return;
        // Check user preferences for task mentions
        const prefsResponse = await notificationPreferencesService.shouldSendNotification(mentionedUser.id, "task_mention");
        if (!prefsResponse.success || !prefsResponse.data) {
            logger.info(`Skipping mention notification for ${mentionedUser.id} - preferences disabled`);
            return;
        }
        return notificationManager.createNotification({
            user_id: mentionedUser.id,
            actor_id: userId,
            type: NOTIFICATION_TYPES.TASK_MENTION,
            entity_type: "task_comment",
            entity_id: commentId,
            message_preview: `${actorData?.full_name || "Someone"} mentioned you in a task comment: ${commentPreview}`,
            data: {
                task_id: taskId,
                task_title: taskTitle,
                comment_id: commentId,
                comment_preview: commentPreview,
                actor_username: actorData?.username,
                actor_name: actorData?.full_name,
                actor_avatar: actorData?.avatar_url,
                mentioned_in: "task_comment",
            },
        });
    });
    await Promise.all(notificationPromises);
}
export class TaskCommentService implements ITaskCommentService {
    readonly name = "TaskCommentService";
    async initialize(): Promise<void> {
        // no-op
    }
    async dispose(): Promise<void> {
        // no-op
    }
    async getTaskComments(taskId: string): Promise<ServiceResponse<TaskComment[]>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from("task_comments")
                .select(`
          *,
          user:profiles!user_id(username, full_name, avatar_url),
          principal:mycelium_principals!principal_id(display_name, kind)
        `)
                .eq("task_id", taskId)
                .order("created_at", { ascending: true });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: (data || []) as TaskComment[] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    async createTaskComment(comment: CreateTaskCommentRequest): Promise<ServiceResponse<TaskComment>> {
        const startTime = Date.now();
        try {
            const { data: { user }, } = await typedSupabase.auth.getUser();
            if (!user) {
                return { success: false, error: "User not authenticated", code: "AUTH_REQUIRED", service: this.name };
            }
            const { data, error } = await typedSupabase
                .from("task_comments")
                .insert({
                ...comment,
                user_id: user.id,
            })
                .select(`
          *,
          user:profiles!user_id(username, full_name, avatar_url),
          principal:mycelium_principals!principal_id(display_name, kind)
        `)
                .single();
            if (error) {
                return { success: false, error: error.message, code: "CREATE_FAILED", service: this.name };
            }
            // Process mentions in the task comment content
            const { mentionedUsers } = await processMentions(comment.content);
            // Get actor and task info for notifications
            let actorData = null;
            let taskTitle: string | null = null;
            if (mentionedUsers.length > 0) {
                const { data: actor } = await typedSupabase
                    .from("profiles")
                    .select("username, full_name, avatar_url")
                    .eq("id", user.id)
                    .single();
                actorData = actor || null;
                const { data: task } = await typedSupabase
                    .from("todos")
                    .select("title")
                    .eq("id", comment.task_id)
                    .single();
                taskTitle = task?.title || null;
                // Create mention notifications with preference checks
                await createMentionNotifications(mentionedUsers, user.id, comment.task_id, data.id, comment.content, actorData, taskTitle);
            }
            // Log the comment activity
            try {
                await typedSupabase.rpc("log_task_activity", {
                    p_task_id: comment.task_id,
                    p_user_id: user.id,
                    p_action_type: "commented",
                    p_details: { comment_content: comment.content },
                });
            }
            catch (activityError) {
                logger.error("Error logging comment activity:", activityError);
            }
            return { success: true, data: data as TaskComment };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "CREATE_FAILED", service: this.name };
        }
    }
    async updateTaskComment(id: string, content: string): Promise<ServiceResponse<TaskComment>> {
        const startTime = Date.now();
        try {
            const { data: { user }, } = await typedSupabase.auth.getUser();
            if (!user) {
                return { success: false, error: "User not authenticated", code: "AUTH_REQUIRED", service: this.name };
            }
            // Fetch the existing comment (need previous content to diff mentions and check permissions)
            const { data: comment, error: fetchError } = await typedSupabase
                .from("task_comments")
                .select("user_id, task_id, content")
                .eq("id", id)
                .single();
            if (fetchError) {
                return { success: false, error: fetchError.message, code: "FETCH_FAILED", service: this.name };
            }
            // Check if user owns the comment
            const isOwner = comment.user_id === user.id;
            // Check if user is admin
            const { data: isAdmin } = await typedSupabase.rpc("has_admin_role", {
                user_id_param: user.id,
            });
            if (!isOwner && !isAdmin) {
                return { success: false, error: "You don't have permission to edit this comment", code: "PERMISSION_DENIED", service: this.name };
            }
            const { data, error } = await typedSupabase
                .from("task_comments")
                .update({
                content,
                updated_at: new Date().toISOString(),
            })
                .eq("id", id)
                .select(`
          *,
          user:profiles!user_id(username, full_name, avatar_url),
          principal:mycelium_principals!principal_id(display_name, kind)
        `)
                .single();
            if (error) {
                return { success: false, error: error.message, code: "UPDATE_FAILED", service: this.name };
            }
            // Diff mentions: only notify users who were NOT already mentioned in the original content.
            // This prevents duplicate notifications on edits that don't add new mentions.
            const { mentionedUsers: previousMentions } = await processMentions(comment.content || "");
            const { mentionedUsers: currentMentions } = await processMentions(content);
            const previousMentionIds = new Set(previousMentions.map((u) => u.id));
            const addedMentions = currentMentions.filter((u) => !previousMentionIds.has(u.id));
            if (addedMentions.length > 0) {
                const { data: actor } = await typedSupabase
                    .from("profiles")
                    .select("username, full_name, avatar_url")
                    .eq("id", user.id)
                    .single();
                const { data: task } = await typedSupabase
                    .from("todos")
                    .select("title")
                    .eq("id", comment.task_id)
                    .single();
                await createMentionNotifications(addedMentions, user.id, comment.task_id, id, content, actor || null, task?.title || null);
            }
            return { success: true, data: data as TaskComment };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "UPDATE_FAILED", service: this.name };
        }
    }
    async deleteTaskComment(id: string): Promise<ServiceResponse<void>> {
        const startTime = Date.now();
        try {
            const { data: { user }, } = await typedSupabase.auth.getUser();
            if (!user) {
                return { success: false, error: "User not authenticated", code: "AUTH_REQUIRED", service: this.name };
            }
            // First, check if the user owns this comment or is an admin
            const { data: comment, error: fetchError } = await typedSupabase
                .from("task_comments")
                .select("user_id, task_id")
                .eq("id", id)
                .single();
            if (fetchError) {
                return { success: false, error: fetchError.message, code: "FETCH_FAILED", service: this.name };
            }
            // Check if user owns the comment
            const isOwner = comment.user_id === user.id;
            // Check if user is admin
            const { data: isAdmin } = await typedSupabase.rpc("has_admin_role", {
                user_id_param: user.id,
            });
            if (!isOwner && !isAdmin) {
                return { success: false, error: "You don't have permission to delete this comment", code: "PERMISSION_DENIED", service: this.name };
            }
            const { error } = await typedSupabase
                .from("task_comments")
                .delete()
                .eq("id", id);
            if (error) {
                return { success: false, error: error.message, code: "DELETE_FAILED", service: this.name };
            }
            // Log the deletion activity
            try {
                await typedSupabase.rpc("log_task_activity", {
                    p_task_id: comment.task_id,
                    p_user_id: user.id,
                    p_action_type: "comment_deleted",
                    p_details: { deleted_comment_id: id },
                });
            }
            catch (activityError) {
                logger.error("Error logging comment deletion activity:", activityError);
            }
            return { success: true, data: undefined };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "DELETE_FAILED", service: this.name };
        }
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const taskCommentService = new TaskCommentService();
